import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from 'react-query';
import cn from "classnames";
import Grid from "@components/Grid/Grid.tsx";
import Row from "@components/Grid/Row.tsx";
import Radio from "@components/new/Radio";
import Column from "@components/Grid/Column.tsx";
import Box from "@components/Box/Box.tsx";
import Title from "@components/Title/Title.tsx";
import { Button, ButtonSize, ButtonType } from "@components/Button";
import TextInput from "@components/TextInput/TextInput.tsx";
import { Loader } from '@components/Loader';
import { useProfile } from '@contexts/ProfileContext';
import { getQuestions, sendAnswers, TQuestions, TAnswer } from "@api/apiClient/surveys";
import { getCurrentLanguage } from '@utils/lang';
import styles from "./PollPage.module.scss";

const PollPage = () => {
  const [savedAnswers, setSavedAnswers] = useState<TAnswer[]>([]);
  const [selected, setSelected] = useState<number | undefined>();
  const [comment, setComment] = useState<string>("");
  const [questionIdx, setQuestionIdx] = useState<number>(0);
  const [isEmptyCommentError, setIsEmptyCommentError] = useState<boolean>(false);
  const currentLanguage = getCurrentLanguage();

  const { get } = useProfile();
  const { t } = useTranslation();
  const tt = (obj: Record<string, string>) => obj[currentLanguage.toUpperCase()] || "";

  const { data: questions, isLoading } = useQuery({
    queryKey: ["getQuestions"],
    queryFn: () => getQuestions(),
    retry: 1,
    select: (data: TQuestions) => data?.questionTemplates,
    onSuccess: () => {
      setQuestionIdx(0);
    },
  });

  const saveAnswerMutation = useMutation({
    mutationFn: ({ isSkipped, answers }: { isSkipped: boolean, answers: TAnswer[] }) => sendAnswers({ isSkipped, answers }),
    onSuccess: () => {
      get();
    },
  });

  const skipAnswerMutation = useMutation({
    mutationFn: () => sendAnswers({ isSkipped: true, answers: [] }),
    onSuccess: () => {
      get();
    },
  });

  const question = questions?.[questionIdx];
  const isCommentVisible = question && question.answerTemplates[question.answerTemplates.length - 1]?.id === selected;

  const onSaveAnswer = () => {
    if (isCommentVisible && !comment) {
      setIsEmptyCommentError(true);
      return;
    }
    const isSend = (questionIdx + 1) >= (questions || []).length; // == is last question
    const answer = {
      questionTemplateId: questions?.[questionIdx]?.id ?? 0,
      answerTemplateId: selected ?? 0,
      detailedAnswer: comment,
    };
    const newAnswers = ([
      ...savedAnswers.filter(v => v.questionTemplateId !== answer.questionTemplateId),
      answer,
    ]);

    if (isSend) {
      saveAnswerMutation.mutate({
        isSkipped: false,
        answers: newAnswers,
      });
    } else {
      setQuestionIdx(questionIdx + 1);
      setSavedAnswers(newAnswers);
    }
  };

  const onSkip = () => {
    skipAnswerMutation.mutate();
  };

  useEffect(() => {
    setComment("");
    setIsEmptyCommentError(false);
  }, [selected])

  useEffect(() => {
    setSelected(undefined);
    setComment("");
    setIsEmptyCommentError(false);
  }, [questionIdx, questions])

  if (isLoading) {
    return (
      <Grid>
        <Row>
          <Column phone={12}>
            <Box pt={8}>
              <Loader alignment="center" size="s" />
            </Box>
          </Column>
        </Row>
      </Grid>
    )
  }

  if (!question) return null;

  return (
    <div className={styles.panel}>
      <Grid>
        <Row>
          <Column phone={12}>
            <Box pb={6}>
              <Title title={tt(question.questionTemplate)} />
            </Box>
          </Column>
        </Row>

        <div className={styles.items}>
          {(question.answerTemplates ?? []).map(option => (
            <div
              key={option.id}
              onClick={() => {
                setSelected(option.id);
              }}
              className={cn(
                styles.item,
                selected === option.id && styles.itemSelected,
              )}
            >
              <Radio
                checked={option.id === selected}
              />
              {tt(option.answerTemplate)}
            </div>
          ))}
        </div>

        {isCommentVisible && (
          <Row>
            <Column phone={12}>
              <Box pt={5}>
                <TextInput
                  name="comment"
                  title={t("userPoll.comment")}
                  value={comment}
                  onChange={(v) => {
                    setComment(v);
                    setIsEmptyCommentError(false);
                  }}
                  maxLength={1000}
                  hasError={isEmptyCommentError}
                  errorMessage={t("userPoll.emptyComment")}
                />
              </Box>
            </Column>
          </Row>
        )}

        <Row>
          <Column phone={12}>
            <Box pt={7} flex="row">
              <Button
                title={t("userPoll.skipBtn")}
                type={ButtonType.SKELETON}
                size={ButtonSize.S}
                onClick={onSkip}
                pending={skipAnswerMutation.isLoading}
                isWide
              />
              <div style={{ width: "20px", flexShrink: 0 }}></div>
              <Button
                title={t("userPoll.confirmBtn")}
                type={ButtonType.PRIMARY}
                size={ButtonSize.S}
                onClick={onSaveAnswer}
                pending={saveAnswerMutation.isLoading}
                disabled={!selected}
                isWide
              />
            </Box>
          </Column>
        </Row>
      </Grid>
    </div>
  );
};

export default PollPage;
